<template>
  <c-loading-area :loading="loading">
    <v-card-title>
      {{ $t('page.makePayment.verification.title') }}
    </v-card-title>
    <v-card-subtitle>
      {{ $t('page.makePayment.verification.subTitle') }}
    </v-card-subtitle>
    <v-card-text>
      <v-row v-if="!bean.verificationNotRequired" no-gutters class="mt-4">
        <v-col cols="12">
          <v-radio-group v-model="bean.verificationCriteria.entryType"
                         @change="clearSearchData"
                         :label="$t('page.makePayment.verification.entryType')"
                         class="mt-0">
            <v-radio class="my-2"
                     :label="$t('page.makePayment.verification.entryType.cid')"
                     value="cid"/>
            <v-radio class="my-2"
                     :label="$t('page.makePayment.verification.entryType.dateOfBirth')"
                     value="dateOfBirth"/>
            <v-radio class="my-2"
                     :label="$t('page.makePayment.verification.entryType.driversLicenseNumber')"
                     value="driversLicenseNumber"/>
          </v-radio-group>
        </v-col>
        <v-col cols="12">
          <div v-if="bean.verificationCriteria.entryType === 'cid'">
            <c-text-field
              v-model="bean.verificationCriteria.cid"
              :label="$t('page.makePayment.verification.entryType.cid')"
              key="cid"
              required
              maxlength="250"
              outlined
              hide-details="auto"
              aria-describedby="dfd75b4a-d981-4322-8553-5655dba351f6"/>
            <field-description id="dfd75b4a-d981-4322-8553-5655dba351f6"
                               message-key="page.makePayment.verification.entryType.cid.description"/>
          </div>
          <div v-if="bean.verificationCriteria.entryType === 'dateOfBirth'">
            <c-date-picker
              v-model="bean.verificationCriteria.dateOfBirth"
              outlined
              hide-details
              :label="$t('page.makePayment.verification.entryType.dateOfBirth')"
              key="dateOfBirth"
              required
              aria-describedby="7859451a-363c-44e0-92a0-d99814572f3f"/>
            <field-description id="7859451a-363c-44e0-92a0-d99814572f3f"
                               message-key="'page.makePayment.verification.entryType.dateOfBirth.description"/>
          </div>
          <div v-if="bean.verificationCriteria.entryType === 'driversLicenseNumber'">
            <c-text-field
              v-model="bean.verificationCriteria.driversLicenseNumber"
              :label="$t('page.makePayment.verification.entryType.driversLicenseNumber')"
              key="driversLicenseNumber"
              required
              maxlength="250"
              outlined
              hide-details="auto"
              aria-describedby="de49414b-a8f9-4e0f-b1a3-da760d0afac8"/>
            <field-description id="de49414b-a8f9-4e0f-b1a3-da760d0afac8"
                               message-key="'page.makePayment.verification.entryType.driversLicenseNumber.description"/>
          </div>
        </v-col>
      </v-row>
      <cutout-area v-else class="mt-6">
        {{ $t('page.makePayment.verification.notRequired') }}
      </cutout-area>
    </v-card-text>
    <v-card-actions class="mt-10 justify-end">
      <c-btn type="primary" @click="save">
        {{ $t('global.continue') }}
      </c-btn>
    </v-card-actions>
  </c-loading-area>
</template>

<script>
import CutoutArea from '@components/support/CutoutArea';
import WorkflowStepMixin from '@/mixins/workflowStep';
import { scrollTo } from '@/application/utility';
import _ from 'lodash';
import axios from 'axios';
import { DateTime } from 'luxon';

export default {
  components: { CutoutArea },
  mixins: [ WorkflowStepMixin ],
  methods: {
    clearSearchData () {
      this.bean.verificationCriteria.cid = null;
      this.bean.verificationCriteria.dateOfBirth = null;
      this.bean.verificationCriteria.driversLicenseNumber = null;
    },
    validate () {
      this.$cove.validation.clear();

      if (!this.bean.verificationNotRequired)
      {
        if (this.bean.verificationCriteria.entryType === 'cid') {
          this.$cove.validation.required(this.bean.verificationCriteria.cid,
            'page.makePayment.verification.entryType.cid', 'cid');
        } else if (this.bean.verificationCriteria.entryType === 'dateOfBirth') {
          this.$cove.validation.required(this.bean.verificationCriteria.dateOfBirth,
            'page.makePayment.verification.entryType.dateOfBirth', 'dateOfBirth');
          this.$cove.validation.date.rejectIfInvalid(this.bean.verificationCriteria.dateOfBirth,
            'page.makePayment.verification.entryType.dateOfBirth', 'dateOfBirth');
        } else if (this.bean.verificationCriteria.entryType === 'driversLicenseNumber') {
          this.$cove.validation.required(this.bean.verificationCriteria.driversLicenseNumber,
            'page.makePayment.verification.entryType.driversLicenseNumber', 'driversLicenseNumber');
        } else {
        // This should only happen with a configuration error
          this.$cove.validation.addError('entryType', 'page.makePayment.verification.validation.entryType');
        }
      }

      return !this.$cove.validation.hasErrors();
    },
    save () {
      let isValid = this.validate();
      if (!isValid)
      {
        scrollTo(this.CONST.ERROR_CONTAINER_SELECTOR);
      }
      else
      {
        if (this.bean.verificationNotRequired)
        {
          this.$emit('save', this.bean);
        }
        else
        {
          this.$cove.block();

          // set the DoB to midnight w/UTC TZ.
          let dob = null;
          if (!_.isNil(this.bean.verificationCriteria.dateOfBirth)) {
            dob = this.$cove.parseDate(this.bean.verificationCriteria.dateOfBirth);
            dob = DateTime.fromObject({ year: dob.get('year'), month: dob.get('month'), day: dob.get('day'), zone: 'utc' }).toISO();
          }
          let query = {
            casePartyUUID: this.bean.selections.casePartyUUID,
            ...(!_.isNil(this.bean.verificationCriteria.cid) && { cid: this.bean.verificationCriteria.cid }),
            ...(!_.isNil(dob) && { birthDate: dob }),
            ...(!_.isNil(this.bean.verificationCriteria.driversLicenseNumber) &&
              { driversLicenseNumber: this.bean.verificationCriteria.driversLicenseNumber })
          };
          axios.get(this.$cove.getAPI({ name: 'api.search.verifycaseparty', query }))
            .then((response) => {
              if (!_.isNil(response.data) && response.data)
              {
                this.$emit('save', this.bean);
              }
              else
              {
              // the entryTypes match the field keys so this should highlight the displayed field.
                this.$cove.validation.addError(this.bean.verificationCriteria.entryType,
                  'page.makePayment.verification.validation.invalid');
              }
            }).finally(() => {
              this.$cove.unblock();
            });
        }
      }
    }
  }
};
</script>
